import React, { useEffect, useMemo, useState, useDeferredValue, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Chip,
    Button,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    SwapVert as SwapVertIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Mic as MicIcon,
    Stop as StopIcon,
} from '@mui/icons-material';
import {
    PO_CATEGORIES,
    getPurchaseOrdersForJobs,
    normalizePoSearch,
} from '../../../services/purchaseOrderService';
import useDictation from '../../../hooks/useDictation';

const ROW_HEIGHT = 32;

// Колонки таблицы PO. width — процент от 100% таблицы (table-layout: fixed).
const COLUMNS = [
    { key: 'po',          label: 'PO #',        width: '14%', sortable: true },
    { key: 'description', label: 'Description', width: '36%', sortable: true },
    { key: 'vendor',      label: 'Vendor',      width: '20%', sortable: true },
    { key: 'costCode',    label: 'Cost Code',   width: '14%', sortable: true },
    { key: 'job',         label: 'Job',         width: '16%', sortable: true },
];

function SortIcon({ state }) {
    if (state === 'asc') return <ArrowUpIcon sx={{ fontSize: 13, color: 'primary.main' }} />;
    if (state === 'desc') return <ArrowDownIcon sx={{ fontSize: 13, color: 'primary.main' }} />;
    return <SwapVertIcon sx={{ fontSize: 13, opacity: 0.35 }} />;
}

const PoRow = React.memo(function PoRow({ po, checked, onToggle, rowIdx }) {
    // Короткие поля (PO #, Cost Code, Job) — однострочно (nowrap), длинные
    // (Description, Vendor) — переносятся (wrap), чтобы не было троеточий.
    const td = (extra = {}, wrap = false) => ({
        padding: '6px',
        borderBottom: '1px solid #eee',
        verticalAlign: 'middle',
        ...(wrap
            ? { whiteSpace: 'normal', wordBreak: 'break-word' }
            : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        ...extra,
    });
    return (
        <tr
            onClick={() => onToggle(po.id)}
            style={{
                cursor: 'pointer',
                background: checked ? '#e8f1ff' : (rowIdx % 2 === 0 ? '#fff' : '#fafafa'),
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
            }}
        >
            <td style={{ padding: '4px', borderBottom: '1px solid #eee', textAlign: 'center', verticalAlign: 'middle' }}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => { e.stopPropagation(); onToggle(po.id); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, accentColor: '#1976d2' }}
                />
            </td>
            <td style={td({ fontWeight: 700, color: '#1976d2' })}>{po._po || '—'}</td>
            <td style={td({}, true)}>{po._description}</td>
            <td style={td({ color: '#475569' }, true)}>{po._vendor}</td>
            <td style={td({ color: '#7c3aed', fontWeight: 600 })}>{po._costCode}</td>
            <td style={td()}>#{po._job}</td>
        </tr>
    );
});

/**
 * Секция в SignTimesheetModal: вкладки Material / Equipment / Subcontractor,
 * поиск, multi-select POs, голосовая заметка foreman'а.
 *
 * Контролируемая через `selectedIds` (Set ids) и `comment` (string).
 * Родитель управляет состоянием — при переходе между customers state
 * перезагружается из соответствующих Map'ов.
 *
 * Props:
 *   - jobCodes: string[]      — фильтруем POs по этим job-кодам
 *   - selectedIds: Set<number>
 *   - onSelectedChange: (Set<number>) => void
 *   - comment: string
 *   - onCommentChange: (string) => void
 *   - selectedPosForOnSign: object[]  (см. getSelectedPos в parent)
 */
export default function SignSelectItemsSection({
    jobCodes,
    selectedIds,
    onSelectedChange,
    onSelectedPosResolved,
    itemNotes,
    onItemNoteChange,
}) {
    const [pos, setPos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [activeTab, setActiveTab] = useState('material');
    const [sort, setSort] = useState(null); // null | { field, dir }

    // Voice dictation — единый хук (одна сессия на всё приложение), iPad-fixes inside.
    // Используется и для общего комментария, и для Description каждого item —
    // поэтому трекаем dictKey: какое именно поле сейчас надиктовывается.
    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation,
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });
    const [dictKey, setDictKey] = useState(null);

    // Стабильный ключ — родитель пересоздаёт массив jobCodes на каждый рендер
    // (он inline в IIFE), поэтому если зависеть от jobCodes напрямую useEffect
    // триггерится бесконечно: load → setPos → re-render → новый array → load → …
    const jobCodesKey = useMemo(
        () => (jobCodes ? [...jobCodes].sort().join('|') : ''),
        [jobCodes]
    );

    // Загрузить POs для текущего набора jobs. Ключ зависит от jobCodesKey —
    // при смене customer'а массив поменяется и POs перезагрузятся.
    useEffect(() => {
        if (!jobCodesKey) {
            setPos([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const codes = jobCodesKey.split('|').filter(Boolean);
        console.log('[SignItems] loading POs for jobs:', codes);
        getPurchaseOrdersForJobs(codes)
            .then(list => {
                if (cancelled) return;
                console.log('[SignItems] loaded', list.length, 'POs');
                setPos(list);
            })
            .catch(err => {
                console.error('[SignItems] load POs failed', err);
                if (!cancelled) setPos([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [jobCodesKey]);

    // Категория-счётчики (общие, без поискового фильтра — чтобы пользователь
    // видел сколько всего на каждой вкладке независимо от поиска).
    const counts = useMemo(() => {
        const c = { material: 0, equipment: 0, subcontractor: 0 };
        for (const p of pos) {
            if (c[p._category] != null) c[p._category]++;
        }
        return c;
    }, [pos]);

    // POs для текущей вкладки + поиск + сортировка.
    const visiblePos = useMemo(() => {
        let list = pos.filter(p => p._category === activeTab);

        const term = normalizePoSearch(deferredSearch.trim());
        if (term) {
            list = list.filter(p => normalizePoSearch(p._searchBlob).includes(term));
        }

        if (sort) {
            const fieldKey = `_${sort.field}`;
            const sign = sort.dir === 'desc' ? -1 : 1;
            list = [...list].sort((a, b) =>
                sign * String(a[fieldKey] || '').localeCompare(String(b[fieldKey] || ''), undefined, { numeric: true, sensitivity: 'base' })
            );
        }

        return list;
    }, [pos, activeTab, deferredSearch, sort]);

    // selectedPos считаем по всему pos (а не visiblePos) — чтобы при переключении
    // вкладки/поиска родитель видел все выбранные.
    const selectedPos = useMemo(() => {
        if (!selectedIds || selectedIds.size === 0) return [];
        return pos.filter(p => selectedIds.has(p.id));
    }, [pos, selectedIds]);

    useEffect(() => {
        if (onSelectedPosResolved) onSelectedPosResolved(selectedPos);
    }, [selectedPos, onSelectedPosResolved]);

    const handleToggle = useCallback((id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onSelectedChange(next);
    }, [selectedIds, onSelectedChange]);

    const handleSelectAllVisible = () => {
        const next = new Set(selectedIds);
        for (const p of visiblePos) next.add(p.id);
        onSelectedChange(next);
    };

    const handleClearAll = () => {
        // Только текущей вкладки — другие выборы не трогаем
        const next = new Set(selectedIds);
        for (const p of pos) {
            if (p._category === activeTab) next.delete(p.id);
        }
        onSelectedChange(next);
    };

    const handleSort = (field) => {
        setSort(prev => {
            if (!prev || prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return null;
        });
    };
    const sortStateFor = (field) => sort && sort.field === field ? sort.dir : 'default';

    // Общий раннер надиктовки: key — уникальный id поля (comment | item:<poId>);
    // append(transcript) дописывает распознанный текст к нужному полю. Повторный тап
    // по активному микрофону останавливает; тап по другому — переключает цель.
    const runDictation = useCallback(async (key, append) => {
        if (!dictationSupported) return;
        if (isListening && dictKey === key) { stopDictation(); return; }
        if (isListening) stopDictation();
        setDictKey(key);
        await startDictation({
            onResult: (transcript) => append(transcript),
            onEnd: () => setDictKey(null),
            onError: () => setDictKey(null),
        });
    }, [dictationSupported, isListening, dictKey, startDictation, stopDictation]);

    const appendText = (cur, transcript) => {
        const base = cur || '';
        const space = base && !base.endsWith(' ') ? ' ' : '';
        return base + space + transcript;
    };

    const toggleItemDictation = (poId) =>
        runDictation(`item:${poId}`, (t) =>
            onItemNoteChange && onItemNoteChange(poId, appendText((itemNotes || {})[poId], t)));

    const selectedInTab = useMemo(() =>
        selectedPos.filter(p => p._category === activeTab).length,
    [selectedPos, activeTab]);

    return (
        <Box>
            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none' } }}
            >
                {PO_CATEGORIES.map(cat => (
                    <Tab
                        key={cat.key}
                        value={cat.key}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <span>{cat.label}</span>
                                <Chip
                                    size="small"
                                    label={counts[cat.key]}
                                    sx={{
                                        height: 18, fontSize: 11, fontWeight: 700,
                                        bgcolor: cat.color, color: '#fff',
                                    }}
                                />
                            </Box>
                        }
                    />
                ))}
            </Tabs>

            {/* Search + counters */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search PO, vendor, description, cost code, job…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography variant="caption" color="text.secondary">
                    {visiblePos.length} {visiblePos.length === 1 ? 'item' : 'items'}
                    {selectedInTab > 0 && ` · ${selectedInTab} selected`}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                {visiblePos.length > 0 && (
                    <Button size="small" onClick={handleSelectAllVisible} variant="text">
                        Select all visible
                    </Button>
                )}
                {selectedInTab > 0 && (
                    <Button size="small" onClick={handleClearAll} variant="text" color="secondary">
                        Clear ({selectedInTab})
                    </Button>
                )}
            </Box>

            {/* Table */}
            <Box sx={{
                mt: 1,
                maxHeight: 210,
                overflow: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: 1,
            }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    tableLayout: 'fixed',
                }}>
                    <colgroup>
                        <col style={{ width: '6%' }} />
                        {COLUMNS.map(c => <col key={c.key} style={{ width: c.width }} />)}
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9' }}>
                        <tr style={{ height: 30 }}>
                            <th style={{ padding: '0 4px', borderBottom: '2px solid #cbd5e1' }}></th>
                            {COLUMNS.map(col => {
                                const state = sortStateFor(col.key);
                                return (
                                    <th
                                        key={col.key}
                                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                                        style={{
                                            padding: '0 6px',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            fontSize: 11,
                                            color: state !== 'default' ? '#1976d2' : '#475569',
                                            borderBottom: '2px solid #cbd5e1',
                                            cursor: col.sortable ? 'pointer' : 'default',
                                            whiteSpace: 'nowrap',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            {col.label}
                                            {col.sortable && <SortIcon state={state} />}
                                        </Box>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={COLUMNS.length + 1} style={{ textAlign: 'center', padding: 24 }}>
                                    <CircularProgress size={20} />
                                </td>
                            </tr>
                        ) : visiblePos.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length + 1}
                                    style={{ textAlign: 'center', padding: '20px 12px', color: '#94a3b8' }}>
                                    No POs found{search ? ' (try clearing the search)' : ''}
                                </td>
                            </tr>
                        ) : (
                            visiblePos.map((po, idx) => (
                                <PoRow
                                    key={po.id}
                                    rowIdx={idx}
                                    po={po}
                                    checked={selectedIds.has(po.id)}
                                    onToggle={handleToggle}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </Box>

            {/* ── Selected items + per-item Description ── компактно: одна строка на PO
               (цветная метка категории + PO# + однострочное поле + микрофон). */}
            {selectedPos.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 600 }}>
                        Selected items ({selectedPos.length}) — optional description
                    </Typography>
                    {selectedPos.map(po => {
                        const cat = PO_CATEGORIES.find(c => c.key === po._category);
                        const key = `item:${po.id}`;
                        const listeningHere = isListening && dictKey === key;
                        return (
                            <Box key={po.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 92, flexShrink: 0 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: cat?.color || '#94a3b8', flexShrink: 0 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', whiteSpace: 'nowrap' }}>
                                        {po._po || '—'}
                                    </Typography>
                                </Box>
                                <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Description…"
                                        value={(itemNotes || {})[po.id] || ''}
                                        onChange={(e) => onItemNoteChange && onItemNoteChange(po.id, e.target.value)}
                                        InputProps={{ sx: { pr: 4.5 } }}
                                    />
                                    {dictationSupported && (
                                        <IconButton
                                            onClick={() => toggleItemDictation(po.id)}
                                            size="small"
                                            sx={{
                                                position: 'absolute', right: 3, top: '50%', transform: 'translateY(-50%)',
                                                p: 0.4,
                                                bgcolor: listeningHere ? 'error.main' : 'primary.main',
                                                color: '#fff',
                                                '&:hover': { bgcolor: listeningHere ? 'error.dark' : 'primary.dark' },
                                            }}
                                        >
                                            {listeningHere ? <StopIcon sx={{ fontSize: 16 }} /> : <MicIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

        </Box>
    );
}
